import { AccessControl } from '@components/layout/AccessControl'
import { RentCreateModal } from '@features/contracts/components/RentCreateModal'
import { EditRentFormModal } from '@features/contracts/components/RentEdit'
import RentPayment from '@features/contracts/components/RentPayment'
import { PaidRentWidget } from '@features/contracts/components/RentWidget'
import { CircleCheck, Clock } from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FlatDetail, RentModel } from 'types/api'

import { Card } from '@/components/ui/card'

interface FlatPaymentsProps {
  flat: FlatDetail
  rents: RentModel[] | undefined
}

function SectionCard({
  title,
  icon: Icon,
  children
}: {
  title: string
  icon: typeof Clock
  children: ReactNode
}) {
  return (
    <Card className="p-5 sm:p-6">
      <h2 className="m-0 mb-6 flex items-center gap-2.5 font-display text-xl font-bold tracking-tight text-ink">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        {title}
      </h2>
      {children}
    </Card>
  )
}

const FlatPayments = ({ flat, rents }: FlatPaymentsProps) => {
  const { t } = useTranslation('contract')

  if (!rents) {
    return <div className="h-40 animate-pulse rounded-2xl bg-ink/[0.04]" />
  }

  // The API lists them by due date: open ones soonest first, the paid
  // history latest first.
  const unpaid = rents.filter((rent) => !rent.paid)
  const paid = rents.filter((rent) => rent.paid).reverse()

  return (
    <div className="space-y-6">
      <AccessControl role="ADMIN">
        <div className="flex justify-end">
          <RentCreateModal flat={flat} />
        </div>
      </AccessControl>
      <SectionCard title={t('rent_table.rents.actual_payments')} icon={Clock}>
        {unpaid.length > 0 ? (
          <div className="space-y-4">
            {unpaid.map((rent) => (
              <div
                key={rent.id}
                className="rounded-2xl border border-ink/[0.08] bg-ink/[0.015] p-4"
              >
                <RentPayment rent={rent} embedded />
                {rent.id && (
                  <div className="mt-3 flex justify-end">
                    <EditRentFormModal rentId={rent.id} initialValues={rent} />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-ink/15 bg-ink/[0.015] px-6 py-10 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CircleCheck className="h-5 w-5" />
            </span>
            <span className="text-sm font-medium text-ink">
              {t('rent_table.rents.no_actual_payments')}
            </span>
          </div>
        )}
      </SectionCard>

      {paid.length > 0 && (
        <SectionCard
          title={t('rent_table.rents.paid_payments')}
          icon={CircleCheck}
        >
          <PaidRentWidget paidRents={paid} />
        </SectionCard>
      )}
    </div>
  )
}

export default FlatPayments
